/**
 * 3DConnexion SpaceMouse — Cargador dinámico de scripts SDK
 *
 * El SDK de 3DConnexion usa globals (ab, _3Dconnexion) y no es compatible
 * con el bundler de CRA/webpack como import directo. Se carga como <script>
 * tag dinámico desde /vendor/3DconnexionJS/.
 *
 * NOTA IMPORTANTE: En 3dconnexion.min.js, `_3Dconnexion` está declarado
 * como `const` (no `var`). En ES2015+, las declaraciones `const`/`let`
 * a nivel de script NO se convierten en propiedades de `window`/`globalThis`,
 * solo quedan como variables léxicas globales. Por eso, después de cargar
 * el script, inyectamos un bridge que asigna `window._3Dconnexion = _3Dconnexion`.
 */

/** Estado interno para evitar cargas duplicadas */
let loadPromise: Promise<void> | null = null;
let loaded = false;

/**
 * Inyectar un <script> tag externo y esperar a que cargue.
 */
function injectScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = false;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
}

/**
 * Inyectar un <script> inline que se ejecuta en el scope global del navegador
 * (fuera del wrapper de webpack). Esto permite acceder a variables léxicas
 * globales como `_3Dconnexion` (declarada con `const`) y asignarlas a `window`.
 */
function injectInlineScript(code: string): void {
  const script = document.createElement('script');
  script.textContent = code;
  document.head.appendChild(script);
  script.remove(); // Limpiar — ya se ejecutó sincrónicamente
}

/**
 * Cargar el SDK de 3DConnexion.
 *
 * - Idempotente: si ya está cargado, retorna inmediatamente.
 * - Carga 3dconnexion.min.js (bundle autocontenido: crypto-js + autobahn +
 *   _3Dconnexion + ActionCache + ImageItem).
 * - Inyecta un bridge para exponer `_3Dconnexion` en `window` (es un `const`
 *   y por tanto no se asigna a `globalThis` automáticamente).
 *
 * @throws Error si el script no puede cargarse
 */
export async function loadSpaceMouseSDK(): Promise<void> {
  if (loaded) return;

  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = (async () => {
    try {
      await injectScript('/vendor/3DconnexionJS/3dconnexion.min.js');

      // _3Dconnexion está declarado como `const` en el script, lo cual significa
      // que NO es una propiedad de `window`/`globalThis`. Solo es accesible como
      // variable léxica global. Un script inline SÍ puede acceder a ella porque
      // se ejecuta en el mismo scope global del navegador (no dentro de webpack).
      injectInlineScript(`
        if (typeof _3Dconnexion !== 'undefined') {
          window._3Dconnexion = _3Dconnexion;
        }
      `);

      if (!window._3Dconnexion) {
        throw new Error(
          '3dconnexion.min.js loaded but _3Dconnexion is not available. ' +
          'The SDK may have failed to initialize.'
        );
      }

      loaded = true;
    } catch (err) {
      loadPromise = null;
      throw err;
    }
  })();

  return loadPromise;
}

/**
 * Obtener la clase _3Dconnexion desde window.
 * Lanza error si el SDK no ha sido cargado.
 */
export function get3DconnexionClass(): any {
  if (!window._3Dconnexion) {
    throw new Error(
      '_3Dconnexion is not loaded. Call loadSpaceMouseSDK() first.'
    );
  }
  return window._3Dconnexion;
}

/**
 * Comprobar si el SDK ya está cargado en memoria.
 */
export function isSDKLoaded(): boolean {
  return loaded && !!window._3Dconnexion;
}

/**
 * Limpiar los scripts inyectados (para testing o cleanup completo).
 */
export function unloadSpaceMouseSDK(): void {
  const el = document.querySelector(
    'script[src="/vendor/3DconnexionJS/3dconnexion.min.js"]'
  );
  if (el) el.remove();

  delete (window as any)._3Dconnexion;
  delete (window as any).ab;

  loaded = false;
  loadPromise = null;
}
